import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import CircularLoading from "../loading/Loading";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import "./ParentMilestoneUpdate.css";
import { useParentContext } from "../context/ParentContext";
import dayjs from "dayjs";

const getYouTubeEmbedUrl = (url) => {
  if (!url) return "";
  const vParam = url.split("v=")[1];
  const id = vParam ? vParam.split("&")[0] : url.split("/").pop();
  return `https://www.youtube.com/embed/${id}`;
};

// This component wraps the iframe using a ref so that its DOM node persists.
const VideoFrame = React.memo(({ videoUrl, width, height }) => {
  const iframeRef = useRef(null);

  // Compute the embed URL only when videoUrl changes.
  const embedUrl = useMemo(() => getYouTubeEmbedUrl(videoUrl), [videoUrl]);

  // On mount and when embedUrl changes, update the iframe's src manually.
  useEffect(() => {
    if (iframeRef.current && iframeRef.current.src !== embedUrl) {
      iframeRef.current.src = embedUrl;
    }
  }, [embedUrl]);

  return (
    <iframe
      ref={iframeRef}
      width={width}
      height={height}
      title="YouTube video player"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      style={{
        borderRadius: "8px",
        boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
        transition: "all 0.3s ease-in-out",
      }}
    />
  );
});

// A simple hook to detect mobile view (adjust threshold if needed)
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 720);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 720);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isMobile;
};

// The MilestoneRow now renders the VideoFrame in a container that is memoized.
const MilestoneRow = React.memo(
  ({ milestone, openMobilePopup, getMediaForMilestone }) => {
    // Compute the media URL from the milestone (useMemo prevents recalculation)
    const mediaUrl = useMemo(
      () => getMediaForMilestone(milestone.Milestone),
      [milestone.Milestone, getMediaForMilestone]
    );
    const isMobile = useIsMobile();
    useEffect(() => {
      console.log("MilestoneRow - isMobile:", isMobile);
    }, [isMobile]);
    if (!mediaUrl) return null;

    // For mobile, wrap the video in a clickable container that opens a popup
    if (isMobile) {
      console.log("object is now mobile");
      return (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => openMobilePopup(mediaUrl)}
        >
          <div style={{ pointerEvents: "none" }}>
            <VideoFrame videoUrl={mediaUrl} width="100%" height="200" />
          </div>
        </div>
      );
    }
    // For desktop, render the VideoFrame directly.
    return (
      <div
      // style={{ position: "relative", display: "inline-block" }}
      >
        <VideoFrame videoUrl={mediaUrl} width="300" height="200" />
      </div>
    );
  }
);

const ParentMilstoneUpdate = () => {
  let { docId, id, childId } = useParams();
  console.log(childId);
  if (id === "null" && docId === "null") {
    docId = "";
    id = "";
  }

  const [milestones, setMilestones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mobilePopupOpen, setMobilePopupOpen] = useState(false);
  const [mobilePopupVideoUrl, setMobilePopupVideoUrl] = useState(null);
  const { parentState } = useParentContext();
  const token = localStorage.getItem("s2stoken");
  const navigate = useNavigate();

  const openMobilePopup = useCallback((url) => {
    console.log(url);
    setMobilePopupVideoUrl(url);
    setMobilePopupOpen(true);
  }, []);

  const closeMobilePopup = useCallback(() => {
    setMobilePopupOpen(false);
    setMobilePopupVideoUrl(null);
  }, []);


  // Your static mediaLinks array.
  const mediaLinks = useMemo(
    () => [
      {
        can_hold_head_up_for_short_periods_while_lying_on_the_stomach:
          "https://youtube.com/shorts/DQV6YiNNBec?si=J0vIH3BC4dIElyPj",
      },
      {
        reaches_out_at_objects:
          "https://youtube.com/shorts/OLh5q9GW1Do?si=78kXnX13_Q2exMj5",
      },
      {
        sits_with_support:
          "https://youtube.com/shorts/XbIlVX8R9Pc?si=jHFQo_rqKDxE3xWH",
      },
      {
        reaches_for_and_grasps_toy:
          "https://youtube.com/shorts/aYci6cwnafU?si=vMZxHzmcrggXTIZ3",
      },
      {
        enjoys_mirror:
          "https://youtube.com/shorts/tWqNyjcwoy8?si=eAZ61lvqDbnFrNTk",
      },
      {
        starts_to_crawl:
          "https://youtube.com/shorts/X7rphRcueKM?si=7g_aKtWJ2TE--Ghi",
      },
      {
        separation_anxiety:
          "https://youtube.com/shorts/2P_t8oc8zLU?si=qcG25awu_79d8ssB",
      },
      {
        retrieves_hidden_toy:
          "https://youtube.com/shorts/GRigaJ89XIQ?si=Udsp9ZUIUZe4JxJq",
      },
      {
        enjoys_peek_a_boo:
          "https://youtube.com/shorts/yMMfpJ-fQOs?si=hkcADWRdjqn7aMdr",
      },
      {
        stands_with_support:
          "https://youtube.com/shorts/YTdMbLPT7WI?si=t0eaPFyJ3rEUqg9C",
      },
      {
        self_feeding_with_finger_foods:
          "https://youtube.com/shorts/TvtQ17eac1g?si=WmzSG6BRqnWDP8mJ",
      },
      {
        stands_without_support:
          "https://youtube.com/shorts/Npp1dBMLAzI?si=MQqJK4VoUherLFZO",
      },
      //{Picks_small_objects_with_two_fingers_"("pincer_grasp): "https://youtube.com/shorts/KtRtnVtpe04?si=4x1Q4uTTj8zqx9e6"},
      {
        picks_small_object_with_two_fingers_pincer_grasp:
          "https://youtube.com/shorts/KtRtnVtpe04?si=4x1Q4uTTj8zqx9e6",
      },
      {
        can_manipulate_toys:
          "https://youtube.com/shorts/6qraFtjvxTw?si=Oek1sZXZzOskgU54",
      },
      {
        point_to_body_parts:
          "https://youtube.com/shorts/PIZSHmam874?si=6AWnGh0RpfKbLvkr",
      },
      {
        builds_tower_of_2_blocks:
          "https://youtube.com/shorts/T0-d0aLmVpI?si=KVF1JQPI6USJAgOQ",
      },
      {
        enjoys_scribbling:
          "https://youtube.com/shorts/uvFKO1b25rE?si=RQR9r7zTNar8OCSw",
      },
      {
        enjoys_picture_books_turns_pages:
          "https://youtube.com/shorts/UFUnwm09q6A?si=SPWsbg75375PVHuz",
      },
      {
        imitates: "https://youtube.com/shorts/vEFoQPnkuQA?si=9pBXSN4LAno_xBX5",
      },
      {
        imitates_horizontal_stroke:
          "https://youtube.com/shorts/AxUrSmf-3Mk?si=lg33kfsBN7aWMWiq",
      },
      {
        handles_spoon:
          "https://youtube.com/shorts/2BxgzJPVNcU?si=jN-2IHkLYn8yqEud",
      },
      {
        pretend_play:
          "https://youtube.com/shorts/oV7R5uHsR5U?si=kRWKKL-LmNwVXerS",
      },
      {
        climbs_up_and_down_one_step_ata_time:
          "https://youtube.com/shorts/21sJaBcUupU?si=aggc0sOLvCES4Cfb",
      },
    ],
    []
  );

  const getMediaForMilestone = useCallback(
    (milestoneName) => {
      const key = milestoneName.toLowerCase().replace(/\s+/g, "_");
      const linkObject = mediaLinks.find((link) => link[key]);
      return linkObject ? linkObject[key] : null;
    },
    [mediaLinks]
  );
  const fetchMilestones = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/get_milestone_tracker?child_id=${childId}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to fetch data");
      }

      console.log("The API Response:", data);
      console.log(data.data[0].MilestoneCheck);

      if (data.success) {
        setMilestones(
          data.data.map((milestone) => ({
            ...milestone,
            milestone_check: milestone["Milestone Check"] === "yes",
            red_flag_check: milestone["Red Flag Check"] === "yes",
          }))
        );
      } else {
        alert(data.message);
      }
    } catch (error) {
      if (error?.message === "Token has expired") {
        localStorage.removeItem("s2stoken");
        navigate("/");
      }
      console.error("Error fetching milestone data:", error);
      alert("Error fetching milestone data.");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchMilestones();
  }, [docId, id, childId]);

  const handleMilestoneCheckboxChange = (index) => {
    const updatedMilestones = [...milestones];
    updatedMilestones[index].milestone_check =
      !updatedMilestones[index].milestone_check;
    updatedMilestones[index].isDirty = true;
    updatedMilestones[index].showMilestoneSaveButton =
      updatedMilestones[index].milestone_check;
    setMilestones(updatedMilestones);
  };

  const handleRedFlagCheckboxChange = (index) => {
    const updatedMilestones = [...milestones];
    updatedMilestones[index].red_flag_check =
      updatedMilestones[index].red_flag_check === "yes" ? "no" : "yes";
    updatedMilestones[index].isDirty = true;
    updatedMilestones[index].showRedFlagSaveButton =
      updatedMilestones[index].red_flag_check === "yes";
    setMilestones(updatedMilestones);
  };

  const handleSaveChanges = async (index, type = null) => {
    try {
      const milestone = milestones[index];

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/update_milestone_tracker`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify({
            patient_id: id || null,
            pediatrician_id: docId || null,
            child_id: childId,
            milestones: [
              {
                milestone: milestone.Milestone,
                milestone_check:
                  type === "milestone"
                    ? milestone.milestone_check
                      ? "yes"
                      : "no"
                    : milestone.milestone_check
                    ? "yes"
                    : "no",
                red_flag: milestone["Red Flag"] || "",
                red_flag_check:
                  type === "redFlag"
                    ? milestone.red_flag_check
                      ? "yes"
                      : "no"
                    : milestone.red_flag_check
                    ? "yes"
                    : "no",
              },
            ],
          }),
        }
      );

      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to update data");
      }

      if (data.success) {
        // alert("Milestone data updated successfully");
        const updatedMilestones = [...milestones];
        if (type === "milestone") {
          updatedMilestones[index].isDirty = false;
          updatedMilestones[index].showMilestoneSaveButton = false;
          updatedMilestones[index].isMilestoneChecked =
            updatedMilestones[index].milestone_check; // Keep this line
        }
        if (type === "redFlag") {
          updatedMilestones[index].isDirty = false;
          updatedMilestones[index].showRedFlagSaveButton = false;
          updatedMilestones[index].isRedFlagChecked =
            updatedMilestones[index].red_flag_check; // Keep this line
        }
        setMilestones(updatedMilestones);
      } else {
        alert(data.message);
      }
    } catch (error) {
      if (error?.message === "Token has expired") {
        localStorage.removeItem("s2stoken");
        navigate("/");
      }
      console.error("Error updating milestone data:", error);
      alert("Error updating milestone data.");
    }
  };
  async function handleDeleteMilestone(milestone) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/delete_milestone_tracker/parent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify({
            pediatrician_id: docId,
            patient_id: id,
            milestone: milestone,
            parent_id: parentState.parent_id,
            child_id: childId,
          }),
        }
      );
      const data = await response.json();
      console.log(data);
      if (!response.ok || data.success === false)
        throw new Error(data.message || "Failed to delete milestone");
      if (data.success) {
        alert("Milestone deleted successfully");
        fetchMilestones();
      }
    } catch (error) {
      console.error("Error deleting milestone data:", error);
      alert("Error deleting milestone data.");
      if (error?.message === "Token has expired") {
        localStorage.removeItem("s2stoken");
        navigate("/");
      }
    }
  }

  function renderWhenToComplete(whenToComplete,index) {
    const splitDate = whenToComplete.split('To')
    console.log('index',index);
    if (index === 0) {
      return `${dayjs(splitDate[0]).format('DD/MMM/YYYY')} - ${dayjs(splitDate[1]).format('DD/MMM/YYYY')}`
    }
    let previousEndDate
    if (index === 17) {
      previousEndDate = milestones[11]["When to Complete"].split('To')[1]
    }else{
      previousEndDate = milestones[index-1]["When to Complete"].split('To')[1]
    }
    return `${dayjs(previousEndDate).format('DD/MMM/YYYY')} - ${dayjs(splitDate[1]).format('DD/MMM/YYYY')}`
  }

  const [language, setLanguage] = useState("english");

  const languages = {
    english: "English",
    hindi: "हिंदी",
    gujarati: "ગુજરાતી",
    bengali: "বাংলা",
    marathi: "मराठी",
    telugu: "తెలుగు",
    tamil: "தமிழ்",
    kannada: "ಕನ್ನಡ",
    odia: "ଓଡ଼ିଆ",
    malayalam: "മലയാളം",
    punjabi: "ਪੰਜਾਬੀ",
    assamese: "অসমীয়া",
  };

  const handleLanguageChange = (e) => {
    setLanguage(e.target.value);
  };

  const getLanguageData = async (selectedLanguage) => {
    try {
      const languageModule = await import(
        `../locals/languages/${selectedLanguage}.json`
      );
      return languageModule.default;
    } catch (error) {
      console.error(`Error loading ${selectedLanguage} translations:`, error);
      return null;
    }
  };


  const [translations, setTranslations] = useState(null);

  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const data = await getLanguageData(language);
        setTranslations(data);
      } catch (error) {
        console.error("Error loading translations:", error);
      }
    };

    loadTranslations();
  }, [language]);

  const translateMilestone = (milestone) => {
    if (!milestone || !translations) return milestone;
    return translations.milestones?.[milestone] || milestone;
  };

  const translateRedFlag = (redFlag) => {
    if (!redFlag || !translations) return redFlag;
    return translations.red_flags?.[redFlag] || redFlag;
  };

  if (isLoading) {
    return <CircularLoading />;
  }

  return (
    <div className="milestone-tracker">
      <div className="headerContainer">
        <button
          className="backButton milestone-btn"
          onClick={() => navigate(`/parent-profile`)}
        >
          <FontAwesomeIcon className="icon" icon={faArrowLeft} />
          <span>Back to Child Profile</span>
        </button>
        <h1 className="heading">Milestone Tracker</h1>

        {/* Replace button with dropdown */}
        {/* <select
          value={language}
          onChange={handleLanguageChange}
          className="language-dropdown"
        >
          {Object.entries(languages).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select> */}
      </div>

      <table>
        <thead>
          <tr>
            <th>Age Range</th>
            <th>When to Complete</th>
            <th>Media</th>
            <th>Milestone</th>
            <th>Check</th>
            <th>Red Flag</th>
            <th>Check</th>
          </tr>
        </thead>
        <tbody>
          {milestones.map((milestone, index) => (
            <tr key={index}>
              {index === 0 ||
              milestone["Age Range"] !== milestones[index - 1]["Age Range"] ? (
                <td
                  rowSpan={
                    milestones.filter(
                      (v) => v["Age Range"] === milestone["Age Range"]
                    ).length
                  }
                >
                  {milestone["Age Range"]}
                </td>
              ) : null}
              {index === 0 ||
              milestone["When to Complete"] !==
                milestones[index - 1]["When to Complete"] ? (
                <td
                  rowSpan={
                    milestones.filter(
                      (v) =>
                        v["When to Complete"] === milestone["When to Complete"]
                    ).length
                  }
                >
                  {renderWhenToComplete(milestone["When to Complete"],index)}
                </td>
              ) : null}

              <td>
                <MilestoneRow
                  milestone={milestone}
                  openMobilePopup={openMobilePopup}
                  getMediaForMilestone={getMediaForMilestone}
                />
              </td>

              <td>{translateMilestone(milestone.Milestone)}</td>
              <td>
                {milestone["Milestone Check"] === "yes" ? (
                  <>
                    <span style={{ color: "green" }}>&#10003;</span>
                    <button
                      className="delete-button"
                      onClick={() => handleDeleteMilestone(milestone.Milestone)}
                      title="Clear record"
                    >
                      Change
                    </button>
                  </>
                ) : (
                  <>
                    {milestone.Milestone && milestone.Milestone !== "" ? (
                      milestone.isMilestoneChecked ? (
                        <>
                          <span style={{ color: "green" }}>&#10003;</span>
                          <button
                            className="delete-button"
                            onClick={() =>
                              handleDeleteMilestone(milestone.Milestone)
                            }
                            title="Clear record"
                          >
                            Change
                          </button>
                        </>
                      ) : (
                        <>
                          <input
                            type="checkbox"
                            checked={milestone.milestone_check}
                            onChange={() =>
                              handleMilestoneCheckboxChange(index)
                            }
                          />
                          {milestone.showMilestoneSaveButton && (
                            <button
                              onClick={() =>
                                handleSaveChanges(index, "milestone")
                              }
                            >
                              Save
                            </button>
                          )}
                        </>
                      )
                    ) : null}
                  </>
                )}
              </td>
              <td>{translateRedFlag(milestone["Red Flag"])}</td>
              <td>
                {milestone["Red Flag Check"] === "yes" ? (
                  <>
                    <span style={{ color: "red" }}>&#10005;</span>
                    <button
                      className="delete-button"
                      onClick={() =>
                        handleDeleteMilestone(milestone["Red Flag"])
                      }
                      title="Clear record"
                    >
                      Change
                    </button>
                  </>
                ) : (
                  <>
                    {milestone["Red Flag"] && milestone["Red Flag"] !== "" ? (
                      milestone.isRedFlagChecked ? (
                        <>
                          <span style={{ color: "red" }}>&#10005;</span>
                          <button
                            className="delete-button"
                            onClick={() =>
                              handleDeleteMilestone(milestone["Red Flag"])
                            }
                            title="Clear record"
                          >
                            Change
                          </button>
                        </>
                      ) : (
                        <>
                          <input
                            type="checkbox"
                            checked={milestone.red_flag_check === "yes"}
                            onChange={() => handleRedFlagCheckboxChange(index)}
                          />
                          {milestone.showRedFlagSaveButton && (
                            <button
                              onClick={() =>
                                handleSaveChanges(index, "redFlag")
                              }
                            >
                              Save
                            </button>
                          )}
                        </>
                      )
                    ) : null}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {mobilePopupOpen && (
        <div className="mobile-popup-overlay" onClick={closeMobilePopup}>
          <div
            className="mobile-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="mobile-popup-close" onClick={closeMobilePopup}>
              X
            </button>
            {mobilePopupVideoUrl &&
              (mobilePopupVideoUrl.includes("youtube.com") ||
                mobilePopupVideoUrl.includes("youtu.be")) && (
                <VideoFrame
                  videoUrl={mobilePopupVideoUrl}
                  width="100%"
                  height="200"
                />
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentMilstoneUpdate;